export default function GamificationSection() {
  const skills = [
    { name: 'Approach stability', icon: 'ri-focus-3-line', color: '#00FFD1' },
    { name: 'Airspeed control', icon: 'ri-speed-line', color: '#00A3E0' },
    { name: 'Radio discipline', icon: 'ri-radio-line', color: '#B4FF39' },
    { name: 'Pattern consistency', icon: 'ri-map-2-line', color: '#00FFD1' },
    { name: 'Workload management', icon: 'ri-dashboard-line', color: '#00A3E0' },
  ];

  const topPilots = [
    { rank: 1, callsign: 'Maverick', score: 98, badge: '🏆', prize: '$1,000 fuel card' },
    { rank: 2, callsign: 'Iceman', score: 97, badge: '🥈', prize: '$500 fuel card' },
    { rank: 3, callsign: 'Goose', score: 96, badge: '🥉', prize: '3 months free' },
  ];

  return (
    <section className="py-16 sm:py-24 lg:py-32 bg-[#08121F] relative overflow-hidden">
      {/* Background accent */}
      <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#00FFD1]/5 to-transparent pointer-events-none"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative">
        {/* Core headline */}
        <div className="text-center mb-12 sm:mb-16 lg:mb-20">
          <h2 className="text-3xl sm:text-5xl lg:text-6xl font-bold mb-4 sm:mb-6 leading-tight">
            Fly safer. Fly sharper.<br />
            <span className="text-[#B4FF39]">Have a little fun with it.</span>
          </h2>
          <p className="text-lg sm:text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Flying well is a skill worth measuring. WingmanFix turns every flight into a learning opportunity and a friendly competition among pilots who care about getting better.
          </p>
        </div>

        {/* Gamification intro */}
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center mb-16 sm:mb-24 lg:mb-32">
          <div>
            <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4 sm:mb-6">Great pilots never stop improving.</h3>
            <p className="text-base sm:text-lg text-gray-300 mb-6 sm:mb-8 leading-relaxed">
              WingmanFix tracks your flying performance across every flight, scoring things pilots actually care about.
            </p>

            <div className="space-y-3 sm:space-y-4 mb-6 sm:mb-8">
              {skills.map((skill, idx) => (
                <div key={idx} className="flex items-center gap-3 sm:gap-4 group cursor-default">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center rounded-lg flex-shrink-0" style={{ backgroundColor: `${skill.color}15` }}>
                    <i className={`${skill.icon} text-lg sm:text-xl`} style={{ color: skill.color }}></i>
                  </div>
                  <p className="text-base sm:text-lg text-white font-medium group-hover:text-[#00FFD1] transition-colors">{skill.name}</p>
                </div>
              ))}
            </div>

            <p className="text-sm sm:text-base text-gray-400 italic">
              See how your flying improves over time and how you stack up against other pilots in the Wingman community.
            </p>
          </div>

          {/* Performance visualization */}
          <div className="bg-gradient-to-br from-[#0D1B2E] to-[#050A14] rounded-3xl p-6 sm:p-8 border border-[#00FFD1]/20 shadow-2xl">
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <h4 className="text-xs sm:text-sm font-semibold text-white uppercase tracking-wider">Your Progress</h4>
              <span className="text-xs text-[#B4FF39] bg-[#B4FF39]/10 px-2 sm:px-3 py-1 rounded-full">+12 pts this month</span>
            </div>

            {/* Score gauge */}
            <div className="relative w-36 h-36 sm:w-48 sm:h-48 mx-auto mb-6 sm:mb-8">
              <svg className="w-full h-full -rotate-90" viewBox="0 0 200 200">
                <circle cx="100" cy="100" r="85" stroke="#1A2942" strokeWidth="16" fill="none" />
                <circle
                  cx="100" cy="100" r="85"
                  stroke="#00FFD1"
                  strokeWidth="16"
                  fill="none"
                  strokeDasharray={`${2 * Math.PI * 85}`}
                  strokeDashoffset={`${2 * Math.PI * 85 * (1 - 0.92)}`}
                  strokeLinecap="round"
                  style={{ filter: 'drop-shadow(0 0 12px #00FFD180)' }}
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-3xl sm:text-5xl font-bold text-[#00FFD1]">92</span>
                <span className="text-xs sm:text-sm text-gray-400 uppercase tracking-wider">Overall Score</span>
              </div>
            </div>

            {/* Mini trend */}
            <div className="grid grid-cols-3 gap-3 sm:gap-4 pt-4 sm:pt-6 border-t border-gray-800">
              <div className="text-center">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Last Flight</p>
                <p className="text-xl sm:text-2xl font-bold text-white">94</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Avg (30d)</p>
                <p className="text-xl sm:text-2xl font-bold text-white">89</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Best Ever</p>
                <p className="text-xl sm:text-2xl font-bold text-[#B4FF39]">96</p>
              </div>
            </div>
          </div>
        </div>

        {/* Leaderboard section */}
        <div className="mb-16 sm:mb-24 lg:mb-32">
          <div className="text-center mb-8 sm:mb-12">
            <h3 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-3 sm:mb-4">The Wingman Leaderboard</h3>
            <p className="text-lg sm:text-xl text-gray-300">Every month the top pilots rise to the top.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 sm:gap-8 max-w-5xl mx-auto mb-8 sm:mb-12">
            {topPilots.map((pilot, idx) => (
              <div
                key={idx}
                className={`relative rounded-3xl p-6 sm:p-8 border-2 transition-all hover:scale-105 ${
                  pilot.rank === 1
                    ? 'bg-gradient-to-br from-[#FFD700]/10 to-[#FFA500]/5 border-[#FFD700]/40'
                    : pilot.rank === 2
                    ? 'bg-gradient-to-br from-[#C0C0C0]/10 to-[#A8A8A8]/5 border-[#C0C0C0]/40'
                    : 'bg-gradient-to-br from-[#CD7F32]/10 to-[#B8860B]/5 border-[#CD7F32]/40'
                }`}
              >
                {pilot.rank === 1 && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-[#FFD700] rounded-full text-xl sm:text-2xl shadow-lg">
                    👑
                  </div>
                )}
                
                <div className="text-center">
                  <div className="text-5xl sm:text-6xl mb-3 sm:mb-4">{pilot.badge}</div>
                  <p className="text-xs sm:text-sm text-gray-400 uppercase tracking-wider mb-2">
                    {pilot.rank === 1 ? 'Top Pilot of the Month' : pilot.rank === 2 ? 'Second Place' : 'Third Place'}
                  </p>
                  <p className="text-xl sm:text-2xl font-bold text-white mb-2">{pilot.callsign}</p>
                  <p className="text-base sm:text-lg text-gray-300 mb-3 sm:mb-4">Score: {pilot.score}</p>
                  <div className="pt-3 sm:pt-4 border-t border-gray-700">
                    <p className="text-xs sm:text-sm font-semibold text-[#00FFD1]">{pilot.prize}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center">
            <p className="text-base sm:text-lg text-gray-300 italic mb-6 sm:mb-8">Because better flying deserves more flying.</p>
            <button className="bg-[#00FFD1] text-[#08121F] px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-bold text-base sm:text-lg hover:bg-[#00FFD1]/90 transition-all whitespace-nowrap cursor-pointer">
              View Full Leaderboard
            </button>
          </div>
        </div>

        {/* Social element */}
        <div className="bg-gradient-to-br from-[#0D1B2E] to-[#0A1628] rounded-3xl p-6 sm:p-10 lg:p-12 border border-[#00FFD1]/20 mb-16 sm:mb-24 lg:mb-32">
          <div className="max-w-4xl mx-auto">
            <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4 sm:mb-6 text-center">Your Wingman circle</h3>
            <p className="text-base sm:text-lg text-gray-300 mb-6 sm:mb-8 text-center leading-relaxed">
              Add friends, compare flights, and see who's flying the cleanest patterns this month.
            </p>

            {/* Friend comparison cards */}
            <div className="grid md:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8">
              {[
                { name: 'Slider', score: 94, trend: '+3', status: 'online' },
                { name: 'Jester', score: 91, trend: '+1', status: 'offline' },
                { name: 'Merlin', score: 88, trend: '+5', status: 'flying' },
              ].map((friend, idx) => (
                <div key={idx} className="bg-[#050A14] rounded-xl p-5 sm:p-6 border border-gray-700 hover:border-[#00FFD1]/40 transition-all">
                  <div className="flex items-center gap-3 mb-3 sm:mb-4">
                    <div className="relative">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-br from-[#00FFD1] to-[#00A3E0] flex items-center justify-center text-white font-bold text-base sm:text-lg">
                        {friend.name[0]}
                      </div>
                      {friend.status === 'online' && (
                        <div className="absolute bottom-0 right-0 w-3 h-3 rounded-full bg-[#B4FF39] border-2 border-[#050A14]"></div>
                      )}
                      {friend.status === 'flying' && (
                        <div className="absolute bottom-0 right-0 w-3 h-3 rounded-full bg-[#00FFD1] border-2 border-[#050A14] animate-pulse"></div>
                      )}
                    </div>
                    <div>
                      <p className="text-white font-semibold text-sm sm:text-base">{friend.name}</p>
                      <p className="text-xs text-gray-500 capitalize">{friend.status}</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xl sm:text-2xl font-bold text-white">{friend.score}</span>
                    <span className="text-xs sm:text-sm font-semibold text-[#B4FF39]">{friend.trend} this week</span>
                  </div>
                </div>
              ))}
            </div>

            <p className="text-center text-sm sm:text-base text-gray-400 italic">
              Because every pilot knows someone who claims they grease every landing.<br />
              <span className="text-[#00FFD1] font-semibold">Now there's a scoreboard.</span>
            </p>
          </div>
        </div>

        {/* Training facility section */}
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center mb-16 sm:mb-24 lg:mb-32">
          <div>
            <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4 sm:mb-6">Training schools that produce sharper pilots.</h3>
            <p className="text-base sm:text-lg text-gray-300 mb-6 sm:mb-8 leading-relaxed">
              WingmanFix highlights flight schools where students consistently demonstrate strong flying habits.
            </p>
            <p className="text-sm sm:text-base text-gray-300 mb-6 sm:mb-8 leading-relaxed">
              Schools with top performing students receive a <span className="text-[#B4FF39] font-semibold">Wingman Training Excellence</span> rating, helping future pilots choose where to train.
            </p>

            <div className="space-y-3 sm:space-y-4">
              {[
                { icon: 'ri-trophy-line', text: 'Better student outcomes', color: '#B4FF39' },
                { icon: 'ri-bar-chart-box-line', text: 'Objective training feedback', color: '#00FFD1' },
                { icon: 'ri-line-chart-line', text: 'Performance tracking across instructors', color: '#00A3E0' },
                { icon: 'ri-award-line', text: 'Recognition on the Wingman training network', color: '#B4FF39' },
              ].map((item, idx) => (
                <div key={idx} className="flex items-center gap-3 sm:gap-4">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center rounded-lg flex-shrink-0" style={{ backgroundColor: `${item.color}15` }}>
                    <i className={`${item.icon} text-lg sm:text-xl`} style={{ color: item.color }}></i>
                  </div>
                  <p className="text-sm sm:text-base text-white">{item.text}</p>
                </div>
              ))}
            </div>

            <p className="text-base sm:text-lg text-[#00FFD1] font-semibold mt-6 sm:mt-8 italic">
              The better your students fly, the higher your school ranks.
            </p>
          </div>

          <div className="bg-gradient-to-br from-[#0D1B2E] to-[#050A14] rounded-3xl p-6 sm:p-8 border border-[#B4FF39]/20 shadow-2xl">
            <div className="flex items-center gap-3 mb-4 sm:mb-6">
              <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-[#B4FF39]/10 rounded-xl">
                <i className="ri-shield-star-line text-xl sm:text-2xl text-[#B4FF39]"></i>
              </div>
              <div>
                <p className="text-xs text-[#B4FF39] uppercase tracking-wider font-semibold">Excellence Rating</p>
                <p className="text-xs sm:text-sm text-gray-400">Top 10% Nationally</p>
              </div>
            </div>

            <h4 className="text-xl sm:text-2xl font-bold text-white mb-2">Skyward Flight Academy</h4>
            <p className="text-xs sm:text-sm text-gray-400 mb-4 sm:mb-6">Santa Monica, CA · KSMO</p>

            <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-4 sm:mb-6">
              <div className="bg-[#0A1628] rounded-xl p-3 sm:p-4 border border-gray-700">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Avg Student Score</p>
                <p className="text-2xl sm:text-3xl font-bold text-[#B4FF39]">91</p>
              </div>
              <div className="bg-[#0A1628] rounded-xl p-3 sm:p-4 border border-gray-700">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Active Students</p>
                <p className="text-2xl sm:text-3xl font-bold text-white">47</p>
              </div>
            </div>

            <div className="space-y-2 sm:space-y-3">
              <div className="flex items-center justify-between text-xs sm:text-sm">
                <span className="text-gray-400">Approach Stability</span>
                <span className="text-white font-semibold">94</span>
              </div>
              <div className="flex items-center justify-between text-xs sm:text-sm">
                <span className="text-gray-400">Radio Discipline</span>
                <span className="text-white font-semibold">92</span>
              </div>
              <div className="flex items-center justify-between text-xs sm:text-sm">
                <span className="text-gray-400">Pattern Consistency</span>
                <span className="text-white font-semibold">89</span>
              </div>
            </div>

            <button className="w-full mt-4 sm:mt-6 bg-[#B4FF39]/10 border border-[#B4FF39]/30 text-[#B4FF39] px-4 sm:px-6 py-2.5 sm:py-3 rounded-xl font-semibold text-sm sm:text-base hover:bg-[#B4FF39]/20 transition-all whitespace-nowrap cursor-pointer">
              View School Profile
            </button>
          </div>
        </div>

        {/* Community section */}
        <div className="text-center max-w-4xl mx-auto">
          <h3 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 sm:mb-8">A community of pilots who care about flying well.</h3>
          <p className="text-lg sm:text-xl text-gray-300 mb-6 sm:mb-8 leading-relaxed">
            WingmanFix isn't about surveillance or scoring for the sake of scoring.
          </p>
          <p className="text-base sm:text-lg text-gray-300 mb-8 sm:mb-12 leading-relaxed">
            It's about building a culture of pilots who want to:
          </p>

          <div className="grid md:grid-cols-2 gap-4 sm:gap-6 max-w-2xl mx-auto mb-8 sm:mb-12">
            {[
              'fly smoother',
              'learn faster',
              'stay sharp',
              'and help each other improve',
            ].map((item, idx) => (
              <div key={idx} className="bg-[#0D1B2E] rounded-xl p-5 sm:p-6 border border-[#00FFD1]/20">
                <p className="text-lg sm:text-xl text-white font-semibold">{item}</p>
              </div>
            ))}
          </div>

          {/* Punchy callouts */}
          <div className="grid md:grid-cols-2 gap-3 sm:gap-4 mb-8 sm:mb-12">
            {[
              'Better pilots compete.',
              'Fly safe. Fly sharp. Climb the leaderboard.',
              'Because smoother flying should show.',
              'Proof of great flying.',
            ].map((line, idx) => (
              <div key={idx} className="bg-gradient-to-r from-[#00FFD1]/10 to-transparent rounded-xl p-3 sm:p-4 border-l-4 border-[#00FFD1]">
                <p className="text-sm sm:text-base text-white font-semibold italic">{line}</p>
              </div>
            ))}
          </div>

          <p className="text-xl sm:text-2xl text-[#B4FF39] font-bold italic">Every flight tells a story.</p>
        </div>
      </div>
    </section>
  );
}